<template>
  <article class="result-item">
    <div class="result-head">
      <el-avatar
        :src="item.authorAvatar"
        :size="40"
        class="result-avatar"
        @click="$emit('open-profile', item.authorId)"
      >
        {{ (item.authorName || "?").charAt(0) }}
      </el-avatar>
      <div class="result-meta">
        <div class="result-author" @click="$emit('open-profile', item.authorId)">
          {{ item.authorName || "用户" }}
        </div>
        <div class="result-time">{{ formatTime(item.createTime) }}</div>
      </div>
    </div>

    <div class="result-content-wrap">
      <div
        class="result-content"
        :class="{ 'is-clamped': !expanded && needClamp }"
        v-html="displayHtml"
      />
      <button
        v-if="needClamp"
        type="button"
        class="result-expand"
        @click="expanded = !expanded"
      >
        {{ expanded ? "收起" : "展开" }}
      </button>
    </div>

    <div v-if="previewImages.length" class="result-images" :class="imageGridClass">
      <el-image
        v-for="(url, idx) in previewImages"
        :key="url + idx"
        :src="url"
        :preview-src-list="allImages"
        :initial-index="idx"
        fit="cover"
        class="result-image"
        preview-teleported
      />
    </div>

    <div v-if="topics.length" class="result-topics">
      <el-tag
        v-for="topic in topics"
        :key="topic.id"
        size="small"
        effect="plain"
        type="primary"
        class="result-topic-tag"
        @click="$emit('select-topic', topic.id)"
      >
        {{ topic.name }}
      </el-tag>
    </div>

    <div class="result-actions">
      <button
        type="button"
        class="result-action"
        :class="{ 'is-active': isLiked }"
        @click="toggleLike"
      >
        <el-icon><IconLike /></el-icon>
        <span>{{ localLikeCount }}</span>
      </button>
      <button
        type="button"
        class="result-action"
        :class="{ 'is-active': commentsOpen }"
        @click="toggleComments"
      >
        <el-icon><ChatDotRound /></el-icon>
        <span>{{ localCommentCount }}</span>
      </button>
      <button
        type="button"
        class="result-action"
        :class="{ 'is-active': isFavorited }"
        @click="toggleFavorite"
      >
        <el-icon><Star /></el-icon>
        <span>{{ localFavoriteCount }}</span>
      </button>
    </div>

    <div
      v-if="commentsOpen"
      class="result-comments"
      :class="{ 'is-empty': !hasCommentList }"
    >
      <div v-loading="commentsLoading">
        <u-comment :config="commentConfig" @submit="handleSubmitComment">
          <template #avatar="slotProps">
            <el-avatar
              :src="
                getCommentUser(slotProps)?.avatar ||
                getCommentUser(slotProps)?.user?.avatar
              "
              :size="28"
              @click.stop="
                $emit(
                  'open-profile',
                  getCommentUser(slotProps)?.uid || getCommentUser(slotProps)?.user?.uid
                )
              "
            >
              {{
                (
                  getCommentUser(slotProps)?.username ||
                  getCommentUser(slotProps)?.user?.username ||
                  "?"
                ).charAt(0)
              }}
            </el-avatar>
          </template>
        </u-comment>
      </div>
    </div>
  </article>
</template>

<script>
import { ChatDotRound, Star } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { UComment } from "undraw-ui";
import IconLike from "@/components/icons/IconLike.vue";
import { formatTime } from "@/utils/format";
import { likePost, unlikePost } from "@/api/postLike";
import { favoritePost, unfavoritePost } from "@/api/postFavorite";
import { getPostComments, publishComment } from "@/api/postComment";
import { getToken } from "@/utils/authStorage";

const PREVIEW_LEN = 160;

export default {
  name: "SearchResultItem",
  components: { ChatDotRound, Star, IconLike, UComment },
  inject: {
    layoutRef: { default: null },
  },
  props: {
    item: { type: Object, required: true },
  },
  emits: ["need-login", "open-profile", "select-topic", "comment-count-change"],
  data() {
    return {
      expanded: false,
      commentsOpen: false,
      commentsLoading: false,
      commentsLoaded: false,
      isLiked: !!this.item.liked,
      likeLoading: false,
      isFavorited: !!this.item.favorited,
      favoriteLoading: false,
      localLikeCount: this.item.likeCount ?? 0,
      localCommentCount: this.item.commentCount ?? 0,
      localFavoriteCount: this.item.favoriteCount ?? 0,
      commentConfig: {
        user: { id: "guest", username: "游客", avatar: "" },
        comments: [],
        show: {
          form: true,
          content: true,
          level: false,
          likes: false,
          address: false,
          homeLink: false,
          reply: true,
        },
        placeholder: "说点什么...",
      },
    };
  },
  computed: {
    displayHtml() {
      return this.item.content || "";
    },
    plainText() {
      return this.displayHtml.replace(/<[^>]+>/g, "").trim();
    },
    needClamp() {
      return this.plainText.length > PREVIEW_LEN;
    },
    topics() {
      return Array.isArray(this.item.topics) ? this.item.topics : [];
    },
    postId() {
      return this.item.postId;
    },
    allImages() {
      const list = Array.isArray(this.item.images)
        ? this.item.images.filter(Boolean)
        : [];
      return list;
    },
    previewImages() {
      return this.allImages.slice(0, 4);
    },
    imageGridClass() {
      const n = this.previewImages.length;
      if (n <= 1) return "is-one";
      if (n === 2) return "is-two";
      if (n === 3) return "is-three";
      return "is-four";
    },
    currentUser() {
      if (!getToken()) return null;
      const u = this.layoutRef?.userInfo;
      return u?.id != null ? u : null;
    },
    hasCommentList() {
      return (this.commentConfig.comments || []).length > 0;
    },
  },
  watch: {
    currentUser: {
      handler(u) {
        if (u) {
          this.commentConfig.user = {
            id: String(u.id),
            username: u.nickName || u.username || "用户",
            avatar: u.avatar || "",
          };
          this.commentConfig.placeholder = "说点什么...";
        } else {
          this.commentConfig.user = { id: "guest", username: "游客", avatar: "" };
          this.commentConfig.placeholder = "请先登录后发表评论";
        }
      },
      immediate: true,
      deep: true,
    },
    "item.likeCount"(v) {
      this.localLikeCount = v ?? 0;
    },
    "item.commentCount"(v) {
      this.localCommentCount = v ?? 0;
    },
    "item.favoriteCount"(v) {
      this.localFavoriteCount = v ?? 0;
    },
    "item.liked"(v) {
      this.isLiked = !!v;
    },
    "item.favorited"(v) {
      this.isFavorited = !!v;
    },
  },
  methods: {
    formatTime,
    getCommentUser(slotProps) {
      return slotProps?.item || slotProps || {};
    },
    async toggleLike() {
      if (!getToken()) {
        this.$emit("need-login");
        return;
      }
      if (this.likeLoading || this.postId == null) return;
      this.likeLoading = true;
      try {
        if (this.isLiked) {
          const res = await unlikePost(this.postId);
          if (res?.data?.code === 200) {
            this.isLiked = false;
            this.localLikeCount = Math.max(0, this.localLikeCount - 1);
          }
        } else {
          const res = await likePost(this.postId);
          if (res?.data?.code === 200) {
            this.isLiked = true;
            this.localLikeCount += 1;
          }
        }
      } catch (e) {
        ElMessage.error("操作失败");
      } finally {
        this.likeLoading = false;
      }
    },
    async toggleFavorite() {
      if (!getToken()) {
        this.$emit("need-login");
        return;
      }
      if (this.favoriteLoading || this.postId == null) return;
      this.favoriteLoading = true;
      try {
        if (this.isFavorited) {
          const res = await unfavoritePost(this.postId);
          if (res?.data?.code === 200) {
            this.isFavorited = false;
            this.localFavoriteCount = Math.max(0, this.localFavoriteCount - 1);
          }
        } else {
          const res = await favoritePost(this.postId);
          if (res?.data?.code === 200) {
            this.isFavorited = true;
            this.localFavoriteCount += 1;
          }
        }
      } catch (e) {
        ElMessage.error("操作失败");
      } finally {
        this.favoriteLoading = false;
      }
    },
    async toggleComments() {
      this.commentsOpen = !this.commentsOpen;
      if (this.commentsOpen && !this.commentsLoaded) {
        await this.loadComments();
      }
    },
    async loadComments() {
      if (this.postId == null) return;
      this.commentsLoading = true;
      try {
        const res = await getPostComments(this.postId);
        if (res?.data?.code === 200) {
          this.commentConfig.comments = res.data.data || [];
          this.commentsLoaded = true;
        }
      } catch (e) {
        ElMessage.error("加载评论失败");
      } finally {
        this.commentsLoading = false;
      }
    },
    async handleSubmitComment(submitParam) {
      if (!getToken()) {
        submitParam?.finish?.();
        this.$emit("need-login");
        return;
      }
      try {
        const res = await publishComment(
          this.postId,
          submitParam.content,
          submitParam.parentId ? Number(submitParam.parentId) : null
        );
        if (res?.data?.code === 200) {
          ElMessage.success(submitParam.parentId ? "回复成功" : "评论成功");
          this.localCommentCount += 1;
          this.$emit("comment-count-change", this.localCommentCount);
          try {
            submitParam?.finish?.();
          } catch (e) {
            console.warn("评论输入框清理失败:", e);
          }
          await this.loadComments();
        } else {
          ElMessage.error(res?.data?.msg || "评论失败");
          try {
            submitParam?.finish?.();
          } catch (e) {
            /* ignore */
          }
        }
      } catch (e) {
        ElMessage.error("评论失败");
        try {
          submitParam?.finish?.();
        } catch (err) {
          /* ignore */
        }
      }
    },
  },
};
</script>

<style scoped>
.result-item {
  padding: 16px 4px;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.result-item:last-child {
  border-bottom: none;
}

.result-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.result-avatar {
  flex-shrink: 0;
  cursor: pointer;
}

.result-meta {
  min-width: 0;
}

.result-author {
  font-weight: 600;
  font-size: 15px;
  color: var(--el-text-color-primary);
  cursor: pointer;
}

.result-author:hover {
  color: var(--el-color-primary);
}

.result-time {
  margin-top: 2px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.result-content-wrap {
  margin-top: 12px;
}

.result-content {
  font-size: 15px;
  line-height: 1.7;
  color: var(--el-text-color-primary);
  word-break: break-word;
  white-space: pre-wrap;
}

.result-content.is-clamped {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
}

.result-expand {
  margin-top: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--el-color-primary);
  font-size: 14px;
  cursor: pointer;
}

.result-expand:hover {
  opacity: 0.85;
}

.result-content :deep(.search-hl) {
  color: var(--el-color-danger);
  font-weight: 600;
  background: transparent;
}

.result-images {
  display: grid;
  gap: 6px;
  margin-top: 12px;
  max-width: 420px;
}

.result-images.is-one {
  grid-template-columns: 1fr;
  max-width: 280px;
}

.result-images.is-two,
.result-images.is-four {
  grid-template-columns: 1fr 1fr;
}

.result-images.is-three {
  grid-template-columns: 1fr 1fr;
}

.result-image {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
}

.result-images.is-one .result-image {
  aspect-ratio: 4 / 3;
}

.result-topics {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.result-topic-tag {
  cursor: pointer;
}

.result-actions {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.result-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  color: var(--el-text-color-secondary);
  cursor: pointer;
  font-size: 13px;
  padding: 4px 0;
}

.result-action:hover,
.result-action.is-active {
  color: var(--el-color-primary);
}

.result-comments {
  margin-top: 12px;
  padding-top: 4px;
}

.result-comments.is-empty :deep(.comment-list-wrapper) {
  display: none;
}
</style>
